import { useCallback, MutableRefObject } from "react";
import { QueryClient } from "@tanstack/react-query";
import { clearServiceWorkerCache } from "@/lib/clearServiceWorkerCache";
import { fetchAccount } from "@/modules/account/service";
import { Account } from "@/types/account";

interface UseImpersonationParams {
  broadcastChannelRef: MutableRefObject<BroadcastChannel | null>;
  setImpersonateCognitoId: (value: string | null) => void;
  setImpersonationReturnPath: (path: string | null) => void;
  impersonationReturnPath: string | null;
  setLoadingImpersonation: (loading: boolean) => void;
  setImpersonationMessage: (message: string) => void;
  setAccount: (account: Account | null) => void;
  locationPathname: string;
  queryClientRef: MutableRefObject<QueryClient | undefined>;
  navigateRef: MutableRefObject<(path: string) => void>;
  tRef: MutableRefObject<(key: string) => string>;
  toastRef: MutableRefObject<
    (options: {
      title: string;
      description?: string;
      variant?: "destructive";
    }) => void
  >;
}

export const useImpersonation = ({
  broadcastChannelRef,
  setImpersonateCognitoId,
  setImpersonationReturnPath,
  impersonationReturnPath,
  setLoadingImpersonation,
  setImpersonationMessage,
  setAccount,
  locationPathname,
  queryClientRef,
  navigateRef,
  tRef,
  toastRef,
}: UseImpersonationParams) => {
  const handleImpersonate = useCallback(
    async (cognitoId: string) => {
      setLoadingImpersonation(true);
      setImpersonationMessage(tRef.current("impersonation.loading.start"));
      try {
        const returnPath = locationPathname;
        setImpersonationReturnPath(returnPath);
        setImpersonateCognitoId(cognitoId);

        if (broadcastChannelRef.current) {
          broadcastChannelRef.current.postMessage({
            type: "impersonation-change",
            impersonateCognitoId: cognitoId,
          });
        }

        await clearServiceWorkerCache();
        queryClientRef.current?.clear();
        await queryClientRef.current?.invalidateQueries();

        const signedInAccount = await fetchAccount();
        if (signedInAccount !== undefined && signedInAccount !== null) {
          if (!signedInAccount.authenticated_as) {
            toastRef.current({
              title: tRef.current("impersonation.error.title"),
              description: tRef.current("impersonation.error.missingData"),
              variant: "destructive",
            });
            setImpersonateCognitoId(null);
            setImpersonationReturnPath(null);
            if (broadcastChannelRef.current) {
              broadcastChannelRef.current.postMessage({
                type: "impersonation-change",
                impersonateCognitoId: null,
              });
            }
            await clearServiceWorkerCache();
            queryClientRef.current?.clear();
            const adminAccount = await fetchAccount();
            if (adminAccount !== undefined) {
              setAccount(adminAccount);
            }
            return;
          }
          setAccount(signedInAccount);
        }

        navigateRef.current("/");
      } finally {
        setLoadingImpersonation(false);
        setImpersonationMessage("");
      }
    },
    [
      locationPathname,
      setImpersonateCognitoId,
      setImpersonationReturnPath,
      setLoadingImpersonation,
      setImpersonationMessage,
      setAccount,
      broadcastChannelRef,
      queryClientRef,
      navigateRef,
      tRef,
      toastRef,
    ],
  );

  const handleStopImpersonation = useCallback(async () => {
    setLoadingImpersonation(true);
    setImpersonationMessage(tRef.current("impersonation.loading.stop"));
    try {
      const returnPath = impersonationReturnPath;
      setImpersonateCognitoId(null);
      setImpersonationReturnPath(null);

      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage({
          type: "impersonation-change",
          impersonateCognitoId: null,
        });
      }

      await clearServiceWorkerCache();
      queryClientRef.current?.clear();
      await queryClientRef.current?.invalidateQueries();

      const signedInAccount = await fetchAccount();
      if (signedInAccount !== undefined) {
        setAccount(signedInAccount);
      }

      if (returnPath) {
        navigateRef.current(returnPath);
      }
    } finally {
      setLoadingImpersonation(false);
      setImpersonationMessage("");
    }
  }, [
    impersonationReturnPath,
    setImpersonateCognitoId,
    setImpersonationReturnPath,
    setLoadingImpersonation,
    setImpersonationMessage,
    setAccount,
    broadcastChannelRef,
    queryClientRef,
    navigateRef,
    tRef,
  ]);

  return {
    handleImpersonate,
    handleStopImpersonation,
  };
};
